/**
 * PostHog Analytics — Calvarius
 *
 * EU cluster: https://eu.i.posthog.com (DSGVO-konform, Daten bleiben in der EU).
 *
 * Lazy: posthog-js wird per dynamischem Import erst nach `requestIdleCallback`
 * (oder Fallback `setTimeout`) geladen — hält das Main-Bundle frei vom
 * ~195 KiB schweren Recorder-Code.
 *
 * Consent-gated: Tracking startet erst nach Zustimmung zur Kategorie
 * "Statistik / Analyse" im Cookie-Banner.
 *
 * Session-Recording: Wird nur auf konversions-relevanten Pfaden aktiviert
 * (Kontakt, Tools, Leistungen). Auf der Startseite und Content-Routen
 * läuft kein Recorder — spart Main-Thread und Bandbreite.
 *
 * HINWEIS DSGVO: Aktuell ist Session-Recording an dieselbe Consent-Kategorie
 * gekoppelt wie Pageview-Analytics (`analytics`). Falls Recording im Banner
 * als separate Kategorie geführt werden soll (z.B. "Verhaltensbasierte
 * Analyse"), muss die Bedingung in `shouldEnableRecording()` und das
 * Consent-Schema in `CookieConsent.tsx` entsprechend erweitert werden.
 */
import type { PostHog } from "posthog-js";

const POSTHOG_HOST = "https://eu.i.posthog.com";
const POSTHOG_KEY =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined) ||
  "phc_tidYWRF659nMVMNFwgnQ8ssURAC87bMeEoq7bjosugTa";

// ---------- Conversion-Pfade (Session-Recording-Allowlist) ----------
const CONVERSION_PATH_PATTERNS = [
  /^\/kontakt(\/|$)/,
  /^\/tools(\/|$)/,
  /^\/leistungen\//,
  /^\/en\/contact(\/|$)/,
  /^\/en\/tools(\/|$)/,
  /^\/en\/services\//,
];

function isConversionPath(pathname: string): boolean {
  return CONVERSION_PATH_PATTERNS.some((re) => re.test(pathname));
}

// ---------- Click-ID persistence ----------
const CLICK_ID_STORAGE = "calv.click_ids.v1";
const CLICK_ID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

type ClickIds = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  ttclid?: string;
  li_fat_id?: string;
  msclkid?: string;
  ts: number;
};

function readClickIds(): ClickIds | null {
  try {
    const raw = localStorage.getItem(CLICK_ID_STORAGE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ClickIds;
    if (Date.now() - parsed.ts > CLICK_ID_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function captureClickIdsFromUrl(): ClickIds | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const keys = ["gclid", "gbraid", "wbraid", "fbclid", "ttclid", "li_fat_id", "msclkid"] as const;
  const found: ClickIds = { ts: Date.now() };
  let hasAny = false;
  for (const k of keys) {
    const v = params.get(k);
    if (v) {
      (found as Record<string, unknown>)[k] = v;
      hasAny = true;
    }
  }
  if (!hasAny) return null;
  const existing = readClickIds();
  const merged: ClickIds = { ...(existing ?? { ts: Date.now() }), ...found, ts: Date.now() };
  try {
    localStorage.setItem(CLICK_ID_STORAGE, JSON.stringify(merged));
  } catch {
    /* ignore */
  }
  return merged;
}

// ---------- Consent gate ----------
type Consent = { analytics: boolean; marketing: boolean };
function readConsent(): Consent | null {
  try {
    const raw = localStorage.getItem("calv.consent.v1");
    if (!raw) return null;
    return JSON.parse(raw) as Consent;
  } catch {
    return null;
  }
}

function shouldEnableRecording(pathname: string): boolean {
  const consent = readConsent();
  if (!consent?.analytics) return false;
  return isConversionPath(pathname);
}

// ---------- Lazy PostHog handle ----------
let _posthog: PostHog | null = null;
let _loadPromise: Promise<PostHog | null> | null = null;
let initialized = false;
let recordingActive = false;
let pendingEvents: Array<(ph: PostHog) => void> = [];
let lastPageviewUrl: string | null = null;
let lastPageviewTs = 0;
const PAGEVIEW_DEDUP_MS = 500;

function scheduleIdle(cb: () => void) {
  if (typeof window === "undefined") return;
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof ric === "function") {
    ric(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 1);
  }
}

async function loadPosthog(): Promise<PostHog | null> {
  if (_posthog) return _posthog;
  if (_loadPromise) return _loadPromise;
  _loadPromise = import("posthog-js").then((mod) => {
    _posthog = mod.default;
    return _posthog;
  }).catch((err) => {
    console.warn("[analytics] posthog-js failed to load", err);
    return null;
  });
  return _loadPromise;
}

function safePageview(url: string) {
  if (!initialized || !_posthog) return;
  const now = Date.now();
  if (url === lastPageviewUrl && now - lastPageviewTs < PAGEVIEW_DEDUP_MS) return;
  lastPageviewUrl = url;
  lastPageviewTs = now;
  _posthog.capture("$pageview", { $current_url: url });
}

async function doInit() {
  if (initialized) return;
  const ph = await loadPosthog();
  if (!ph) return;

  const consent = readConsent();
  const optedIn = consent?.analytics === true;
  const initialPathname = typeof window !== "undefined" ? window.location.pathname : "/";
  // Recorder beim Init nur dann aktiv, wenn Consent + Conversion-Pfad.
  const initialRecording = optedIn && isConversionPath(initialPathname);

  ph.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: !initialRecording,
    disable_surveys: true,
    capture_dead_clicks: false,
    capture_performance: { web_vitals: false },
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-sensitive]",
    },
    // @ts-expect-error — runtime-supported option
    maskAllText: false,
    opt_out_capturing_by_default: !optedIn,
    loaded: (loaded) => {
      const ids = captureClickIdsFromUrl() ?? readClickIds();
      if (ids) {
        const { ts: _ts, ...idsOnly } = ids;
        loaded.register(idsOnly);
        loaded.setPersonProperties(idsOnly);
      }
      if (optedIn && typeof window !== "undefined") {
        safePageview(window.location.href);
      }
    },
  });

  initialized = true;
  recordingActive = initialRecording;

  // Consent-Änderungen
  window.addEventListener("calv:consent", (e: Event) => {
    const detail = (e as CustomEvent<Consent>).detail;
    if (!_posthog) return;
    if (detail?.analytics) {
      _posthog.opt_in_capturing();
      safePageview(window.location.href);
      // Recorder ggf. nachträglich starten, wenn aktuell auf Conversion-Pfad
      maybeStartRecording(window.location.pathname);
      const queued = pendingEvents;
      pendingEvents = [];
      queued.forEach((fn) => fn(_posthog!));
    } else {
      _posthog.opt_out_capturing();
      _posthog.reset();
      lastPageviewUrl = null;
      lastPageviewTs = 0;
      recordingActive = false;
    }
  });
}

/**
 * Aktiviert Session-Recording, wenn Consent vorliegt und der Pfad in der
 * Conversion-Allowlist liegt — und Recording nicht bereits läuft.
 *
 * Bewusst kein Stop beim Verlassen eines Conversion-Pfads: läuft die Session
 * weiter, behalten wir Kontext (User landet auf /kontakt → navigiert kurz
 * weg → kommt zurück; das Recording bleibt zusammenhängend).
 */
export function maybeStartRecording(pathname: string) {
  if (!initialized || !_posthog) return;
  if (recordingActive) return;
  if (!shouldEnableRecording(pathname)) return;
  try {
    _posthog.startSessionRecording();
    recordingActive = true;
  } catch (err) {
    console.warn("[analytics] startSessionRecording failed", err);
  }
}

export function initAnalytics() {
  if (typeof window === "undefined") return;
  if (initialized || _loadPromise) return;
  scheduleIdle(() => {
    void doInit();
  });
}

// ---------- Typed event API ----------
export type ConversionEvent =
  | "contact_form_submitted"
  | "calendly_booked"
  | "angebot_angefordert"
  | "blog_lead_magnet_downloaded"
  | "newsletter_subscribed"
  | "phone_number_clicked";

type EventProps = Record<string, string | number | boolean | null | undefined>;

function safeCapture(name: string, props?: EventProps) {
  if (typeof window === "undefined") return;
  const consent = readConsent();
  if (!consent?.analytics) {
    pendingEvents.push((ph) => ph.capture(name, props));
    return;
  }
  if (!initialized) {
    // Init triggern und Event puffern
    pendingEvents.push((ph) => ph.capture(name, props));
    initAnalytics();
    return;
  }
  if (!_posthog) return;
  _posthog.capture(name, props);
}

export function trackEvent(name: string, props?: EventProps) {
  safeCapture(name, props);
}

export function trackConversion(name: ConversionEvent, props?: EventProps) {
  const ids = readClickIds();
  const eventId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  const enriched: EventProps = {
    ...(props ?? {}),
    event_id: eventId,
    is_conversion: true,
    gclid: ids?.gclid ?? null,
    fbclid: ids?.fbclid ?? null,
    ttclid: ids?.ttclid ?? null,
    li_fat_id: ids?.li_fat_id ?? null,
    msclkid: ids?.msclkid ?? null,
  };
  safeCapture(name, enriched);
}

export function identifyUser(distinctId: string, props?: EventProps) {
  if (!initialized || !_posthog) return;
  _posthog.identify(distinctId, props);
}

export function trackPageview(url: string) {
  if (typeof window === "undefined") return;
  const consent = readConsent();
  if (!consent?.analytics || !initialized) return;
  safePageview(url);
}
